/**
 * 직원 소개 — 추가 · 수정 모달과 노출 스위치 (/my/staff/ · page-partner-staff.php).
 *
 * 틀(이름 · 직급 · 네이버 예약 · 인스타그램)은 PHP 가 그리고, 대표 사진과 설명글은 설정 화면과 같은
 * 공용 컴포넌트(imagePicker · richText)를 비워 둔 자리에 끼운다. 여닫기는 initModal.js 가 맡는다.
 *
 * 모달은 하나다. [+ 직원 추가]로 열면 빈 추가 모드, 카드를 누르면 그 카드의
 * data-staff-edit 값으로 채운 수정 모드가 된다. 같은 대상을 다시 열면 고치던 내용이 그대로 있고,
 * 다른 대상으로 바꿔 열 때만 비우고 새로 채운다.
 *
 * 카드의 노출 스위치는 누르는 즉시 저장하고 목록을 새로고침한다.
 * 저장 · 삭제 · 순서는 소식 · 케어와 같은 조각(partnerForm.js)으로 admin-ajax(Partner_Staff)를 거쳐 매장 사이트로 간다.
 */

import { createImagePicker } from './imagePicker';
import { lockModal } from './initModal';
import { readOrder } from './initOrderList';
import {
  postForm,
  reloadWith as reloadWithKey,
  setNotice,
  showFlash,
  showToast,
} from './partnerForm';
import { createModeEditor } from './modeEditor';

/** 새로고침한 화면에 띄울 알림을 맡겨 두는 자리 */
const FLASH_KEY = 'qrshop_staff_flash';

const reloadWith = (message, url = '') => reloadWithKey(FLASH_KEY, message, url);

const InitPartnerStaff = () => {
  const form = document.querySelector('[data-staff-form]');

  if (!form) {
    return;
  }

  showFlash(FLASH_KEY);

  // 폼은 모달 body 이고, 저장·삭제 버튼과 결과 안내는 footer 에 있다 (저장 버튼은 form 속성으로 이어진다)
  const dialog = form.closest('dialog');
  const status = dialog?.querySelector('[data-staff-status]');
  const submit = dialog?.querySelector(`[type="submit"][form="${form.getAttribute('id')}"]`);
  const deleteButton = dialog?.querySelector('[data-staff-delete]');
  const locked = form.querySelector('[data-staff-locked]');
  const contentFields = form.querySelectorAll('[data-staff-content]');
  const idInput = form.elements.namedItem('post_id');
  const nameInput = form.elements.namedItem('name');
  const positionInput = form.elements.namedItem('position');
  const naverInput = form.elements.namedItem('link_naver');
  const instagramInput = form.elements.namedItem('link_instagram');

  // 사진은 고르는 즉시 webp data URL 로 바뀌어 여기 담긴다 (되돌리면 빈 값 = 원래 사진 유지)
  let imageDataUrl = '';

  // 지금 모달에 들어 있는 대상 — 'add' 또는 'edit-{id}'
  let target = 'add';

  const setStatus = (message, state = 'error') => setNotice(status, message, state);

  const picker = createImagePicker({
    initialUrl: '',
    side: true,
    onStaged: (dataUrl) => {
      imageDataUrl = dataUrl || '';
    },
    onError: (message) => setStatus(message),
  });

  const body = createModeEditor({ placeholder: '설명글을 입력해 주세요.' });

  form.querySelector('[data-staff-image]')?.append(picker.element);
  form.querySelector('[data-staff-body]')?.append(body.element);

  // 라벨 줄 오른쪽 — Simple / Pro 전환 버튼
  form.querySelector('[data-staff-body-mode]')?.replaceWith(body.switcher);

  // 고치기 시작하면 지난번 안내는 걷어 낸다
  form.addEventListener('input', () => setStatus(''));

  /** 원장이 고치면 깨지는 글(관리자가 넣은 영상 등)은 설명글 칸을 감추고 안내를 띄운다 */
  const setEditable = (editable) => {
    contentFields.forEach((field) => {
      field.hidden = !editable;
    });

    if (locked) {
      locked.hidden = editable;
    }
  };

  /** 모든 칸을 처음 상태로 */
  const clear = (imageUrl = '') => {
    form.reset();

    // hidden 입력은 value 가 곧 기본값이라 reset() 으로 비워지지 않는다.
    // 남겨 두면 [추가]가 방금 고치던 직원을 덮어쓴다 — 직접 비운다.
    idInput.value = '';
    picker.setInitial(imageUrl);
    body.setContent('');
    setStatus('');
    setEditable(true);
  };

  const openAdd = () => {
    if ('add' === target) {
      return;
    }

    target = 'add';
    clear();

    if (deleteButton) {
      deleteButton.hidden = true;
    }
  };

  /**
   * @param {{ id: number, name: string, position: string, link_naver: string, link_instagram: string,
   *   image: string, body: string, mode: string, locked: boolean, mine: boolean }} staff
   */
  const openEdit = (staff) => {
    const next = `edit-${staff.id}`;

    if (next === target) {
      return;
    }

    target = next;
    clear(staff.image || '');

    idInput.value = String(staff.id);
    nameInput.value = staff.name || '';
    positionInput.value = staff.position || '';
    naverInput.value = staff.link_naver || '';
    instagramInput.value = staff.link_instagram || '';
    body.setContent(staff.body || '', staff.mode);
    setEditable(true !== staff.locked);

    if (deleteButton) {
      deleteButton.hidden = true !== staff.mine;
    }
  };

  // 여는 일은 initModal.js 가 한다. 여기서는 무엇으로 열었는지 보고 내용만 채운다.
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('[data-partner-add]')) {
      openAdd();
      return;
    }

    const card = event.target.closest?.('[data-staff-edit]');

    if (!card) {
      return;
    }

    try {
      openEdit(JSON.parse(card.dataset.staffEdit || '{}'));
    } catch (error) {
      setStatus('직원 정보를 불러오지 못했습니다. 새로고침한 뒤 다시 시도해 주세요.');
    }
  });

  // ── 저장 · 삭제 ─────────────────────────────────────────────────────
  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const name = nameInput instanceof HTMLInputElement ? nameInput.value.trim() : '';

    if ('' === name) {
      setStatus('이름을 입력해 주세요.');
      nameInput?.focus();
      return;
    }

    // 추가 모드면 어떤 경우에도 새 직원이다 (남의 글을 덮어쓰지 않도록 한 번 더 막는다)
    const id = 'add' === target ? 0 : Number(idInput.value || 0);

    // 사진을 올리느라 몇 초 걸릴 수 있다 — 그동안 모달을 잠근다
    const unlock = lockModal(dialog, submit, '저장 중…');

    try {
      const result = await postForm(form, form.dataset.action || '', {
        id,
        name,
        position: (positionInput?.value || '').trim(),
        link_naver: (naverInput?.value || '').trim(),
        link_instagram: (instagramInput?.value || '').trim(),
        image: imageDataUrl,
        body: body.getHTML(),
        mode: body.getMode(),
      });

      // 끝났으면 먼저 닫는다. 새 직원은 노출 목록에 들어가므로 그 목록으로 옮기고,
      // 고친 것은 보던 목록 그대로 다시 그린다.
      dialog.close();
      unlock();
      reloadWith(result.message || '저장했습니다.', id ? '' : form.dataset.listUrl || '');
    } catch (error) {
      unlock();
      setStatus(error.message);
    }
  });

  deleteButton?.addEventListener('click', async () => {
    const id = Number(idInput.value || 0);

    if (!id) {
      return;
    }

    // 매장 사이트에서는 휴지통으로 가지만 원장은 되살릴 방법이 없으므로 한 번 묻는다
    if (!window.confirm('이 직원을 삭제할까요?\n매장 페이지에서도 바로 사라집니다.')) {
      return;
    }

    const unlock = lockModal(dialog, deleteButton, '삭제 중…');

    try {
      const result = await postForm(form, form.dataset.deleteAction || '', { id });

      dialog.close();
      unlock();
      reloadWith(result.message || '삭제했습니다.');
    } catch (error) {
      unlock();
      setStatus(error.message);
    }
  });

  // ── 카드의 노출 스위치 — 바꾸는 즉시 저장한다 ─────────────────────────
  document.addEventListener('change', async (event) => {
    const toggle = event.target;

    if (!(toggle instanceof HTMLInputElement) || !toggle.matches('[data-staff-show]')) {
      return;
    }

    const id = Number(toggle.dataset.staffShow || 0);
    const shown = toggle.checked;
    const label = toggle.closest('.toggle')?.querySelector('.toggle__label');

    toggle.disabled = true;

    if (label) {
      label.textContent = '저장 중…';
    }

    try {
      const result = await postForm(form, form.dataset.showAction || '', {
        id,
        shown: shown ? 1 : 0,
      });

      reloadWith(
        result.message || (shown ? '매장 페이지에 보입니다.' : '매장 페이지에서 감췄습니다.')
      );
    } catch (error) {
      toggle.checked = !shown;
      toggle.disabled = false;

      if (label) {
        label.textContent = toggle.checked ? '노출' : '비노출';
      }

      showToast(error.message, 'error');
    }
  });

  // ── 순서 변경 모달 — 노출 중인 직원이 둘 이상일 때만 있다 ──────────────
  const orderDialog = document.getElementById('staff-order-modal');
  const orderList = orderDialog?.querySelector('[data-order-list]');
  const orderSave = orderDialog?.querySelector('[data-order-save]');
  const orderStatus = orderDialog?.querySelector('[data-order-status]');

  orderSave?.addEventListener('click', async () => {
    setNotice(orderStatus, '');

    const unlock = lockModal(orderDialog, orderSave, '저장 중…');

    try {
      const result = await postForm(form, form.dataset.orderAction || '', {
        ids: readOrder(orderList).join(','),
      });

      orderDialog.close();
      unlock();
      reloadWith(result.message || '순서를 바꿨습니다.');
    } catch (error) {
      unlock();
      setNotice(orderStatus, error.message);
    }
  });
};

export default InitPartnerStaff;
